import { EVENT_NAMES, EVENT_PARAMETERS } from "./EventConstants";
import { ActivityMessage, getActivityKind } from "./activitySignals";

const MAX_TRACKED_TRIGGERS = 100;

type ResponseMilestone = "first_thinking_received" | "first_tool_received" | "first_text_received";

interface MilestoneMessage extends ActivityMessage {
  is_user_message?: boolean;
}

type TrackAnalytics = (eventName: string, properties: Record<string, unknown>) => void;

interface RecordResponseMilestonesArgs {
  messages: MilestoneMessage[];
  threadId?: string | null;
  startedAt?: number | null;
  track: TrackAnalytics;
  now?: number;
}

const seenByTrigger = new Map<string, Set<ResponseMilestone>>();

const milestoneForMessage = (message: MilestoneMessage): ResponseMilestone | null => {
  if (message.message_type === "text_delta") return "first_text_received";
  if (message.message_type === "chat" && message.is_user_message !== true) return "first_text_received";

  const activityKind = getActivityKind(message);
  if (activityKind === "thinking") return "first_thinking_received";
  if (activityKind === "tool" || activityKind === "query" || activityKind === "sources") {
    return "first_tool_received";
  }
  return null;
};

const seenForTrigger = (threadId: string, triggerId: string): Set<ResponseMilestone> => {
  const key = `${threadId}:${triggerId}`;
  let seen = seenByTrigger.get(key);
  if (!seen) {
    seen = new Set<ResponseMilestone>();
    seenByTrigger.set(key, seen);
    if (seenByTrigger.size > MAX_TRACKED_TRIGGERS) {
      const oldestKey = seenByTrigger.keys().next().value;
      if (oldestKey) seenByTrigger.delete(oldestKey);
    }
  }
  return seen;
};

export const getResponseWaitDurationMs = (
  startedAt: number | null | undefined,
  now: number = Date.now(),
): number | null => {
  if (typeof startedAt !== "number" || !Number.isFinite(startedAt)) return null;
  return Math.max(0, now - startedAt);
};

export const recordResponseMilestones = ({
  messages,
  threadId,
  startedAt,
  track,
  now = Date.now(),
}: RecordResponseMilestonesArgs): void => {
  const latencyMs = getResponseWaitDurationMs(startedAt, now);
  if (latencyMs === null || typeof track !== "function" || !Array.isArray(messages) || !threadId) return;

  messages.forEach((message) => {
    const milestone = milestoneForMessage(message);
    const triggerId = message.trigger_id;
    if (!milestone || !triggerId) return;

    const seen = seenForTrigger(threadId, triggerId);
    if (seen.has(milestone)) return;
    seen.add(milestone);

    track(EVENT_NAMES.RESPONSE_MILESTONE_RECEIVED, {
      [EVENT_PARAMETERS.THREAD_ID]: threadId,
      [EVENT_PARAMETERS.TRIGGER_ID]: triggerId,
      [EVENT_PARAMETERS.MESSAGE_ID]: message.id,
      [EVENT_PARAMETERS.RESPONSE_MILESTONE]: milestone,
      [EVENT_PARAMETERS.LATENCY_MS]: latencyMs,
    });
  });
};

export const resetResponseMilestoneTelemetry = (): void => {
  seenByTrigger.clear();
};
